import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Building2, Plus, Search, Loader2, Download, Trash2, Edit3, Flag, AlertTriangle
} from "lucide-react";
import { FALLBACK_OFFICES, FALLBACK_INCIDENTS } from "@/data/dummyData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const emptyForm = {
  building_name: "", office_number: "", period_start: "", period_end: "", incidents: "",
};

export default function LogisticsOffices() {
  const [items, setItems] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("office");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchBuilding, setSearchBuilding] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: activeTab };
      if (searchBuilding) params.building_name = searchBuilding;
      const res = await axios.get(`${API}/logistics/offices-parking/items`, { params, headers: getHeaders() });
      const fallback = activeTab === "office" ? FALLBACK_OFFICES.filter(o => o.type === "office") : FALLBACK_OFFICES.filter(o => o.type === "parking");
      setItems(res.data.length > 0 ? res.data : fallback);
    } catch {
      const fallback = activeTab === "office" ? FALLBACK_OFFICES.filter(o => o.type === "office") : FALLBACK_OFFICES.filter(o => o.type === "parking");
      setItems(fallback);
      toast.error("Failed to load. Showing demo data.");
    }
    setLoading(false);
  }, [activeTab, searchBuilding]);

  const fetchIncidents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/logistics/offices-parking/incidents`, { headers: getHeaders() });
      setIncidents(res.data.length > 0 ? res.data : FALLBACK_INCIDENTS);
    } catch { setIncidents(FALLBACK_INCIDENTS); }
  }, []);

  useEffect(() => { fetchItems(); fetchIncidents(); }, [fetchItems, fetchIncidents]);

  const openAdd = () => { setEditId(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      building_name: item.building_name || "",
      office_number: item.office_number || "",
      period_start: item.period_start ? item.period_start.slice(0, 10) : "",
      period_end: item.period_end ? item.period_end.slice(0, 10) : "",
      incidents: item.incidents || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, type: activeTab };
      if (editId) {
        await axios.put(`${API}/logistics/offices-parking/items/${editId}`, payload, { headers: getHeaders() });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/logistics/offices-parking/items`, payload, { headers: getHeaders() });
        toast.success("Created");
      }
      setDialogOpen(false);
      fetchItems();
      fetchIncidents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/logistics/offices-parking/items/${deleteId}`, { headers: getHeaders() });
      toast.success("Deleted");
      setDeleteId(null);
      fetchItems();
    } catch { toast.error("Failed to delete"); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/logistics/offices-parking/export`, { headers: getHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "logistics_offices_parking.csv"; a.click();
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">OFFICES & PARKING</h1>
            <p className="text-slate-600">Office & parking management</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="rounded-none border border-slate-200">
            <TabsTrigger value="office" className="rounded-none data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" /> Offices
            </TabsTrigger>
            <TabsTrigger value="parking" className="rounded-none data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" /> Parking
            </TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <Button onClick={openAdd} className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-none text-white">
              <Plus className="w-4 h-4 mr-1" /> Add {activeTab === "office" ? "Office" : "Parking Slot"}
            </Button>
            <Button onClick={handleExport} variant="outline" className="rounded-none border-slate-300">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center gap-3">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search by building..." value={searchBuilding} onChange={(e) => setSearchBuilding(e.target.value)} className="w-64 bg-app border-slate-300 rounded-none" />
          <Button onClick={fetchItems} size="sm" className="rounded-none bg-amber-600 hover:bg-amber-500"><Search className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>

        <TabsContent value={activeTab} className="mt-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No {activeTab === "office" ? "offices" : "parking slots"} found.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-app border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-600">Building</th>
                    <th className="text-left p-3 font-semibold text-slate-600">{activeTab === "office" ? "Office #" : "Slot #"}</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Period Start</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Period End</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Incidents</th>
                    <th className="text-left p-3 font-semibold text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-app">
                      <td className="p-3 font-medium text-slate-800">{item.building_name}</td>
                      <td className="p-3 text-slate-600">{item.office_number}</td>
                      <td className="p-3 text-xs text-slate-500">{item.period_start ? item.period_start.slice(0, 10) : "-"}</td>
                      <td className="p-3 text-xs text-slate-500">{item.period_end ? item.period_end.slice(0, 10) : "-"}</td>
                      <td className="p-3">
                        {item.incidents ? (
                          <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 flex items-center gap-1 w-fit">
                            <Flag className="w-3 h-3" /> Flagged
                          </span>
                        ) : <span className="text-xs text-slate-400">-</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(item)} className="p-1 text-slate-400 hover:text-amber-600"><Edit3 className="w-4 h-4" /></button>
                          <button onClick={() => setDeleteId(item.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Incidents Section */}
      {incidents.length > 0 && (
        <div className="bg-white border border-red-200 p-4">
          <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Recent Incidents (30 days)
          </h3>
          <div className="space-y-2">
            {incidents.map((inc) => (
              <div key={inc.id} className="bg-red-50 p-3 border-l-4 border-red-500">
                <p className="text-sm font-medium text-slate-800">{inc.building_name} / {inc.office_number}</p>
                <p className="text-xs text-slate-600 mt-1">{inc.incidents}</p>
                <p className="text-xs text-slate-400 mt-1">{inc.updated_at?.slice(0, 10)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">{editId ? "Edit" : "Add"} {activeTab === "office" ? "Office" : "Parking Slot"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Building Name" value={form.building_name} onChange={(e) => setForm((f) => ({ ...f, building_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Input placeholder={activeTab === "office" ? "Office Number" : "Slot Number"} value={form.office_number} onChange={(e) => setForm((f) => ({ ...f, office_number: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500 mb-1 block">Period Start</label><Input type="date" value={form.period_start} onChange={(e) => setForm((f) => ({ ...f, period_start: e.target.value }))} className="rounded-none bg-app border-slate-300" /></div>
              <div><label className="text-xs text-slate-500 mb-1 block">Period End</label><Input type="date" value={form.period_end} onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))} className="rounded-none bg-app border-slate-300" /></div>
            </div>
            <div><label className="text-xs text-slate-500 mb-1 block">Reported Incidents</label>
              <textarea placeholder="Describe any incidents..." value={form.incidents} onChange={(e) => setForm((f) => ({ ...f, incidents: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> {editId ? "Update" : "Create"}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader><AlertDialogTitle>Delete?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
